import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Group, Paper, Select, Skeleton, Stack, Table, Text, Title } from "@mantine/core";
import { useAudit } from "../api/admin";
import type { AuditEntry } from "../api/types";
import { msToZonedText } from "../time/parse";
import { useZone } from "../time/useZone";
import { FieldDiff } from "../ui/FieldDiff";

const ACTIONS = [
  "",
  "segment_update",
  "segment_approve",
  "segment_unapprove",
  "segment_star",
  "segment_split",
  "segment_join",
  "segment_unjoin",
  // Merging was removed in favour of joining, which leaves the rows where they are. The
  // entries it wrote are still in the table, and this list is what makes them findable.
  "segment_merge",
  "segment_reset",
  "segment_delete",
  "segment_artwork_pin",
  "segment_artwork_unpin",
  "artwork_pin",
  "artwork_unpin",
  "artwork_upload",
  "setting_update",
  "setting_reset",
  "login",
];

/**
 * The audit trail.
 *
 * Read-only by construction: the API has no route that writes here, and the rows are the
 * record of who moved a boundary and when. It is also the only place a bad automatic
 * approval can be traced back to the pass that made it.
 */
export function AuditPage() {
  const [action, setAction] = useState("");
  const { t } = useTranslation("admin");
  // Built here rather than as a module-level constant: the "all" row is copy, and a constant
  // built at import time would freeze it in whatever language was active then.
  const actionOptions = ACTIONS.map((value) => ({
    // The value is the audit table's own action name, an identifier rather than a sentence.
    value,
    label: value || t("auditPage.actionAll"),
  }));
  const { data, isLoading } = useAudit({ action: action || undefined, limit: 200 });

  return (
    <Paper p="md" m={20} style={{ overflowY: "auto", minHeight: 0 }}>
      <Title order={2} fz="lg" lh={1.5} mb={6}>
        {t("auditPage.heading")}
      </Title>
      <Text size="xs" c="dimmed" maw="70ch" mb={14}>
        {t("auditPage.blurb")}
      </Text>

      <Group gap={8} mb={10} fz={12} c="dimmed">
        <span>{t("auditPage.actionLabel")}</span>
        <Select
          value={action}
          data={actionOptions}
          aria-label={t("auditPage.actionLabel")}
          onChange={(next) => setAction(next ?? "")}
        />
      </Group>

      {isLoading ? (
        <>
          <Skeleton height={20} width="40%" mb={4} />
          <Stack gap={6}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} height={20} />
            ))}
          </Stack>
        </>
      ) : (
        <Box style={{ overflowX: "auto" }}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("auditPage.colWhen")}</Table.Th>
                <Table.Th>{t("auditPage.colWho")}</Table.Th>
                <Table.Th>{t("auditPage.colAction")}</Table.Th>
                <Table.Th>{t("auditPage.colEntity")}</Table.Th>
                <Table.Th>{t("auditPage.colChange")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((entry) => (
                <Row key={entry.id} entry={entry} />
              ))}
              {!data?.length && (
                <Table.Tr>
                  <Table.Td colSpan={5}>{t("auditPage.empty")}</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

function Row({ entry }: { entry: AuditEntry }) {
  const zone = useZone();
  const { t } = useTranslation("admin");
  return (
    <Table.Tr>
      <Table.Td style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {msToZonedText(Date.parse(entry.ts), zone.name)}
      </Table.Td>
      <Table.Td>
        {entry.username ?? (
          <Text component="span" fz="inherit" c="dimmed">
            {t("auditPage.system")}
          </Text>
        )}
      </Table.Td>
      <Table.Td>{entry.action}</Table.Td>
      <Table.Td>
        {entry.entity}
        {entry.entity_id ? ` #${entry.entity_id}` : ""}
      </Table.Td>
      <Table.Td>
        <FieldDiff before={entry.before} after={entry.after} />
      </Table.Td>
    </Table.Tr>
  );
}
